import { useLocation, Link } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronRight, Home } from "lucide-react";
import { useCampaignStore, useChatStore, useDraftStore, useInboxStore } from "@/stores";
import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AreaSwitch } from "./area-switch";

interface BreadcrumbItemData {
  displayText: string;
  path: string;
}

// Route segments whose capitalized form reads wrong ("Crm").
const SEGMENT_LABELS: Record<string, string> = {
  crm: "Global CRM",
  "batch-status": "Batch status",
};

export default function Header() {
  const location = useLocation();
  const { campaign } = useCampaignStore();
  const { activeCreatorName } = useInboxStore();
  const { activeDraftCreator } = useDraftStore();
  const { conversations, sharedConversations } = useChatStore();
  const isMobile = useIsMobile();

  const pathSegments = location.pathname
    .split("/")
    .filter((segment) => segment);
  // Staff-only /manager area: Home and the breadcrumb root point there.
  const areaRoot = pathSegments[0] === "manager" ? "/manager" : "/dashboard";

  // The open Amy chat's title (own + shared-with-you rails) for the breadcrumb
  // and the tab title — same pattern as the campaign name below.
  const amyChatId = pathSegments[1] === "amy" ? pathSegments[2] : undefined;
  const amyChatTitle = amyChatId
    ? [...conversations, ...sharedConversations].find((c) => c.id === amyChatId)?.title
    : undefined;

  React.useEffect(() => {
    if (
      pathSegments[1] === "campaigns" &&
      pathSegments.length > 2 &&
      campaign
    ) {
      document.title = `Uplodio | ${campaign?.campaign_name}`;
      return;
    }
    if (amyChatTitle) {
      document.title = `Uplodio | ${amyChatTitle}`;
      return;
    }
    document.title = `Uplodio | ${SEGMENT_LABELS[pathSegments[1]] ?? pathSegments[1]}`;
  }, [pathSegments, amyChatTitle, campaign]);

  // Build displayable breadcrumb items (excluding the "dashboard"/"manager" root)
  const items: BreadcrumbItemData[] = React.useMemo(() => {
    return pathSegments
      .map((segment, index) => {
        if (index === 0 && (segment === "dashboard" || segment === "manager")) return null;

        const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
        const isId = segment.match(/^[0-9a-f]{24}$/);
        const isInboxCreatorId =
          isId && index > 0 && pathSegments[index - 1] === "inbox";
        const isDraftsCreatorId =
          isId && index > 0 && pathSegments[index - 1] === "drafts";
        const isAmyChatId = isId && index > 0 && pathSegments[index - 1] === "amy";

        let displayText;
        if (isInboxCreatorId && activeCreatorName) {
          displayText = activeCreatorName;
        } else if (isDraftsCreatorId && activeDraftCreator) {
          displayText = activeDraftCreator.username;
        } else if (isAmyChatId) {
          displayText = amyChatTitle || "Chat";
        } else if (isId) {
          displayText = campaign?.campaign_name || segment;
        } else {
          displayText =
            SEGMENT_LABELS[segment] ??
            segment.charAt(0).toUpperCase() + segment.slice(1);
        }

        displayText = displayText.replace(/_/g, " ");

        return { displayText, path };
      })
      .filter(Boolean) as BreadcrumbItemData[];
  }, [
    pathSegments,
    campaign,
    activeCreatorName,
    activeDraftCreator,
    amyChatTitle,
  ]);

  // On mobile with 3+ items, collapse middle segments into ellipsis
  const shouldCollapse = isMobile && items.length > 2;

  const renderItems = () => {
    if (items.length === 0) return null;

    if (!shouldCollapse) {
      // Desktop: render all items
      return items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <React.Fragment key={item.path}>
            <BreadcrumbSeparator>
              <ChevronRight className="h-4 w-4" />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              {isLast ? (
                <BreadcrumbPage>{item.displayText}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>{item.displayText}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        );
      });
    }

    // Mobile collapsed: first > ... > last
    const first = items[0];
    const last = items[items.length - 1];
    const middle = items.slice(1, -1);

    return (
      <>
        {/* First item */}
        <BreadcrumbSeparator>
          <ChevronRight className="h-4 w-4" />
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={first.path}>{first.displayText}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {/* Ellipsis dropdown with middle items */}
        <BreadcrumbSeparator>
          <ChevronRight className="h-4 w-4" />
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon-sm" variant="ghost" className="data-[state=open]:bg-muted">
                <BreadcrumbEllipsis />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuGroup>
                {middle.map((item) => (
                  <DropdownMenuItem key={item.path} asChild className="max-w-64 h-7">
                    <Link to={item.path}>
                      <span className="truncate">{item.displayText}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>

        {/* Last item (current page) */}
        <BreadcrumbSeparator>
          <ChevronRight className="h-4 w-4" />
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <BreadcrumbPage>{last.displayText}</BreadcrumbPage>
        </BreadcrumbItem>
      </>
    );
  };

  return (
    <div className="flex flex-row justify-between items-center gap-2 w-full bg-chrome min-h-12 px-2.5 border-b lg:radius-full:bg-transparent lg:radius-full:border-b-0">
      <div className="flex flex-row items-center gap-5 text-sm min-w-0">
        <SidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={areaRoot}>
                  <Home className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Home</span>
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {renderItems()}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <AreaSwitch />
    </div>
  );
}